import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Mail, Linkedin, MapPin, Instagram, ChevronUp } from "lucide-react";
import { companyInfo } from "../data/data.js";

const LogoSVG = () => (
  <img
    src="https://res.cloudinary.com/djem0fq9t/image/upload/v1756908588/logo_auh3jf.png"
    alt="Accelrix Logo"
    className="h-10 w-auto mr-3"
    width="160"
    height="40"
    loading="lazy"
    decoding="async"
  />
);

const Footer = () => {
  const [showButton, setShowButton] = useState(false);
  const footerRef = useRef(null);

  const {
    name: companyName,
    email: companyEmail,
    location: companyLocation,
    linkedInUrl,
    instagramUrl,
  } = companyInfo;

  const currentYear = new Date().getFullYear();
  const linkClass =
    "text-base text-gray-400 hover:text-accelrix-blue transition-colors duration-200";

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  useEffect(() => {
    const handleScroll = () => {
      if (footerRef.current) {
        const footerRect = footerRef.current.getBoundingClientRect();
        setShowButton(footerRect.top <= window.innerHeight);
      }
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  return (
    <footer
      ref={footerRef}
      className="relative text-gray-300 py-16 px-6 lg:px-12 transition-colors duration-500 shadow-inner border-t border-gray-800"
      style={{
        background:
          "linear-gradient(160deg, #0c0f11 0%, #111416 30%, #0a0b0c 100%)",
      }}
    >
      {/* Decorative dots */}
      <div className="absolute inset-0 bg-dots-pattern opacity-[0.06] pointer-events-none"></div>

      {/* Main footer content */}
      <div className="relative z-10 max-w-6xl mx-auto grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-8 pb-12 border-b border-gray-800">
        {/* Company */}
        <div className="flex flex-col items-start lg:col-span-2">
          <Link to="/" className="flex items-center mb-6">
            <LogoSVG />
            <span className="text-4xl font-extrabold bg-gradient-to-r from-logo-gradient-start to-logo-gradient-end text-transparent bg-clip-text drop-shadow-md">
              {companyName}
            </span>
          </Link>
          <p className="text-sm leading-relaxed text-gray-400 max-w-xs">
            Empowering the next generation of tech innovators through hands-on,
            free internship programs and real-world experience.
          </p>
          <p className="mt-3 text-sm flex items-center text-gray-400">
            <MapPin className="mr-2 text-accelrix-blue w-5 h-5" />
            <span>{companyLocation}</span>
          </p>
        </div>

        {/* Quick Links */}
        <div>
          <h3 className="text-lg font-semibold text-gray-100 mb-5 uppercase tracking-wide">
            Quick Links
          </h3>
          <ul className="space-y-3">
            <li>
              <Link to="/" className={linkClass}>
                Home
              </Link>
            </li>
            <li>
              <Link to="/about" className={linkClass}>
                About Us
              </Link>
            </li>
            <li>
              <Link to="/contact" className={linkClass}>
                Contact
              </Link>
            </li>
            <li>
              <Link to="/verify" className={linkClass}>
                Verify
              </Link>
            </li>
          </ul>
        </div>

        {/* Our Services */}
        <div>
          <h3 className="text-lg font-semibold text-gray-100 mb-5 uppercase tracking-wide">
            Our Services
          </h3>
          <ul className="space-y-3">
            <li>
              <Link to="/internships" className={linkClass}>
                Internships
              </Link>
            </li>
            <li>
              <Link to="/services" className={linkClass}>
                Consulting
              </Link>
            </li>
          </ul>
        </div>

        {/* Legal */}
        <div>
          <h3 className="text-lg font-semibold text-gray-100 mb-5 uppercase tracking-wide">
            Legal
          </h3>
          <ul className="space-y-3">
            <li>
              <Link to="/privacy-policy" className={linkClass}>
                Privacy Policy
              </Link>
            </li>
            <li>
              <Link to="/terms-and-conditions" className={linkClass}>
                Terms & Conditions
              </Link>
            </li>
            <li>
              <Link to="/code-of-conduct" className={linkClass}>
                Code of Conduct
              </Link>
            </li>
          </ul>
        </div>

        {/* Get In Touch */}
        <div>
          <h3 className="text-lg font-semibold text-gray-100 mb-5 uppercase tracking-wide">
            Get In Touch
          </h3>
          <ul className="space-y-3 text-sm">
            <li className="flex items-center text-gray-400 hover:text-accelrix-blue transition-colors duration-200">
              <Mail className="mr-2 text-accelrix-blue w-5 h-5" />
              <a href={`mailto:${companyEmail}`}>{companyEmail}</a>
            </li>
            <li className="flex items-center text-gray-400 hover:text-accelrix-blue transition-colors duration-200">
              <Linkedin className="mr-2 text-accelrix-blue w-5 h-5" />
              <a href={linkedInUrl} target="_blank" rel="noopener noreferrer">
                LinkedIn
              </a>
            </li>
            <li className="flex items-center text-gray-400 hover:text-accelrix-blue transition-colors duration-200">
              <Instagram className="mr-2 text-accelrix-blue w-5 h-5" />
              <a href={instagramUrl} target="_blank" rel="noopener noreferrer">
                Instagram
              </a>
            </li>
          </ul>
        </div>
      </div>

      {/* Copyright */}
      <div className="relative z-10 mt-1 pt-6 text-center text-sm text-gray-500">
        &copy; {currentYear} {companyName}. All rights reserved.
      </div>

      {/* Scroll To Top Button */}
      {showButton && (
        <button
          onClick={handleScrollToTop}
          className="fixed bottom-6 right-6 p-3 cta-hover-effect btn-shape"
          aria-label="Scroll to top"
        >
          <ChevronUp size={24} />
        </button>
      )}
    </footer>
  );
};

export default Footer;
